/**
 * 模板详情/编辑页 — 统一样式
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { templateApi } from '../services/templateApi';
import { toast, confirm } from '../store/toastStore';
import { TemplateDetail } from '../types';
import { HexColorPicker } from 'react-colorful';

const FONTS = ['宋体','黑体','楷体','仿宋','微软雅黑','Times New Roman','Arial','Tahoma','Calibri','Cambria'];
const SIZES = [9,10,10.5,11,12,14,16,18,20,22,24,26,28,36,42,48];
const SPACINGS = ['1.0','1.15','1.25','1.5','1.75','2.0','2.5','3.0'];
const ALIGNS = [{v:'LEFT (0)',l:'左'},{v:'CENTER (1)',l:'居中'},{v:'RIGHT (2)',l:'右'},{v:'JUSTIFY (3)',l:'两端'}];
const COLORS = ['000000','333333','666666','999999','BD0000','CC0000','FF0000','006600','008000','00AA00','0000CC','0000FF','6600CC','800080','FF6600','FF9900'];
const PAGE_DIMENSIONS:Record<string,[number,number]> = {A4:[595.3,841.9],A3:[841.9,1190.6],A5:[419.5,595.3],Letter:[612,792],Legal:[612,1008]};

const C_LABELS:Record<string,string> = {
  page_setup:'页面设置', header_footer:'页眉页脚', heading_styles:'标题样式',
  body_style:'正文样式', list_styles:'列表样式', table_styles:'表格样式', toc_styles:'目录样式', image_styles:'图片样式'
};
const K_LABELS:Record<string,string> = {
  page_size:'纸张', orientation:'方向', margin_top:'上边距', margin_bottom:'下边距', margin_left:'左边距', margin_right:'右边距',
  gutter:'装订线', header_distance:'页眉距', footer_distance:'页脚距', header_text:'页眉', footer_text:'页脚',
  Heading1:'一级标题',Heading2:'二级标题',Heading3:'三级标题',Heading4:'四级标题',Heading5:'五级标题',
  'BulletList':'无序列表','NumberList':'有序列表', TableHeader:'表头',TableBody:'正文行',
  TOC1:'一级目录',TOC2:'二级目录',TOC3:'三级目录',
  alignment:'对齐', spacing_before:'段前间距', spacing_after:'段后间距',
  font_name:'字体', font_size:'字号', bold:'加粗', italic:'斜体', color:'颜色', line_spacing:'行距',
  space_before:'段前', space_after:'段后', first_line_indent:'首行缩进', left_indent:'左缩进',
};
const STYLE_FIELDS = ['font_name','font_size','bold','italic','color','alignment','line_spacing','space_before','space_after','first_line_indent','left_indent'];
const PAGE_FIELDS = ['page_size','orientation','margin_top','margin_bottom','margin_left','margin_right','gutter','header_distance','footer_distance'];
const HF_FIELDS = ['header_text','footer_text'];
const IMG_FIELDS = ['alignment','spacing_before','spacing_after'];
const BODY_FIELDS = ['font_name','font_size','bold','italic','color','alignment','line_spacing','space_before','space_after','first_line_indent','left_indent'];
const STYLE_NAMES:Record<string,string[]> = {
  heading_styles: ['Heading1','Heading2','Heading3','Heading4','Heading5'],
  list_styles: ['BulletList','NumberList'], table_styles: ['TableHeader','TableBody'], toc_styles: ['TOC1','TOC2','TOC3'],
};

const inputCls = "w-full text-xs bg-white border border-border rounded-lg px-2.5 py-2 focus:outline-none focus:border-[#0052d9] focus:ring-1 focus:ring-[#0052d9]/20 transition-all";
const selectCls = "w-full text-xs bg-white border border-border rounded-lg px-2 py-2 focus:outline-none focus:border-[#0052d9] focus:ring-1 focus:ring-[#0052d9]/20 transition-all appearance-none";

const TemplateDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [tpl, setTpl] = useState<TemplateDetail|null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [form, setForm] = useState({ template_name:'', category:'', industry:'', description:'', is_public:false });
  const [si, setSi] = useState<any>({});
  const [colorPickerOpen, setColorPickerOpen] = useState('');

  useEffect(() => { if (id) load(); }, [id]);

  const load = async () => {
    if (!id) return; setLoading(true);
    try { const t = await templateApi.getTemplate(parseInt(id)); setTpl(t); setForm({ template_name:t.template_name, category:t.category||'', industry:t.industry||'', description:t.description||'', is_public:t.is_public }); setSi(t.style_info||{}); }
    catch { navigate('/templates'); } finally { setLoading(false); }
  };

  const upd = (path: string[], v: any) => { setSi((prev:any) => {
    const n = JSON.parse(JSON.stringify(prev)); let cur=n;
    for(let i=0;i<path.length-1;i++)cur=cur[path[i]]=cur[path[i]]||{};
    const key=path[path.length-1];
    if(v===undefined||v===''||v===null)delete cur[key]; else cur[key]=v;
    if(path[0]==='page_setup' && (key==='page_size'||key==='orientation')) {
      const dims=PAGE_DIMENSIONS[cur.page_size];
      if (dims) {
        const landscape=String(cur.orientation||'').toLowerCase()==='landscape';
        const [shortSide,longSide]=dims;
        cur.page_width=landscape?longSide:shortSide;
        cur.page_height=landscape?shortSide:longSide;
      }
    }
    return n;
  }); };
  const getFieldType = (f:string) => { if(f==='bold'||f==='italic')return'bool'; if(f==='font_size'||f==='font_name')return'select'; if(f==='alignment')return'select'; if(f==='line_spacing')return'select'; if(f==='color')return'color'; return'text'; };
  const getFieldOpts = (f:string) => { if(f==='font_name')return[FONTS,undefined]; if(f==='font_size')return[SIZES,undefined]; if(f==='alignment')return[ALIGNS.map((a:any)=>a.v),ALIGNS.map((a:any)=>a.l)]; if(f==='line_spacing')return[SPACINGS,undefined]; if(f==='color')return[COLORS,COLORS.map((c:string)=>c||'默认')]; return[undefined,undefined]; };

  const cell = (path: string[], val: any, type: string, opts?: any, labels?: any) => {
    if (type==='bool') return (
      <button type="button" onClick={() => upd(path, !val)} className="inline-flex items-center focus:outline-none">
        <div className={`w-8 h-4 rounded-full transition-colors flex items-center ${val ? 'bg-brand-700' : 'bg-accent'}`}>
          <div className={`w-3 h-3 rounded-full bg-white shadow-sm transform transition-transform mx-0.5 ${val ? 'translate-x-3.5' : ''}`} />
        </div>
      </button>
    );
    if (type==='select') {
      const o = opts||[]; const lk = path[path.length-1];
      if (lk === 'font_name') return <select value={val||''} onChange={e => upd(path, e.target.value||undefined)} className={selectCls} style={{fontFamily:val||'sans-serif'}}>{o.map((v:any)=><option key={v} value={v} style={{fontFamily:v}}>{v}</option>)}</select>;
      if (lk === 'font_size') return <select value={val!=null?String(val):''} onChange={e => upd(path, e.target.value||undefined)} className={selectCls}>{o.map((v:any)=>v+' pt').map((t:string,i:number)=><option key={o[i]} value={o[i]}>{t}</option>)}</select>;
      if (lk === 'line_spacing') return <select value={val!=null?String(val):''} onChange={e => upd(path, e.target.value||undefined)} className={selectCls}>{o.map((v:any)=><option key={v} value={v}>{v}</option>)}</select>;
      return <select value={val!=null?String(val):''} onChange={e => upd(path, e.target.value||undefined)} className={selectCls}>{o.map((v:any,i:number)=><option key={v} value={String(v)}>{labels?.[i]||v}</option>)}</select>;
    }
    if (type==='color') {
      const pk = path.join('.');
      return (
        <div className="relative">
          <button type="button" onClick={() => setColorPickerOpen(cp => cp===pk?'':pk)} className={`w-7 h-7 rounded border shadow-sm ${val?'border-border':'border-dashed border-border'}`} style={{background:val?'#'+val:undefined}} />
          {colorPickerOpen===pk && (
            <div className="absolute z-50 mt-1">
              <div className="fixed inset-0" onClick={() => setColorPickerOpen('')} />
              <div className="relative bg-white p-3 rounded-xl shadow-[0_8px_30px_rgba(0,0,0,0.12)] border border-border">
                <HexColorPicker color={'#'+(val||'000000')} onChange={c => upd(path, c.replace('#',''))} />
                <button type="button" onClick={() => { upd(path, undefined); setColorPickerOpen(''); }} className="mt-2 w-full text-[11px] text-muted-foreground hover:text-foreground py-1 border-t border-border">清除颜色</button>
              </div>
            </div>
          )}
        </div>
      );
    }
    // 数字类型字段用 step 控制精度
    const isNum = ['space_before','space_after','first_line_indent','left_indent',
      'margin_top','margin_bottom','margin_left','margin_right','gutter',
      'header_distance','footer_distance'].includes(path[path.length-1]);
    return <input type={isNum?'number':'text'} step={isNum?'any':undefined} value={val!=null?String(val):''} onChange={e => upd(path, e.target.value||undefined)} className={inputCls} />;
  };

  // 对齐按钮组
  const alignCell = (path: string[], val: any) => {
    const btns = [
      {v:'LEFT (0)', icon:'M4 6h8M4 12h12M4 18h8', label:'左对齐'},
      {v:'CENTER (1)', icon:'M4 6h16M6 12h12M4 18h16', label:'居中'},
      {v:'RIGHT (2)', icon:'M12 6h8M8 12h12M12 18h8', label:'右对齐'},
      {v:'JUSTIFY (3)', icon:'M4 6h16M4 12h16M4 18h16', label:'两端'},
    ];
    return (
      <div className="flex gap-0.5 bg-accent rounded-lg p-0.5">
        {btns.map(a => (
          <button key={a.v} onClick={() => upd(path, val===a.v ? undefined : a.v)} className={`px-2 py-1 rounded-md transition-all ${val===a.v ? 'bg-white text-primary shadow-sm font-bold' : 'text-muted-foreground hover:text-foreground'}`} title={a.label}>
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d={a.icon}/></svg>
          </button>
        ))}
      </div>
    );
  };

  const KV_FIELD_TYPES:Record<string,string> = {
    page_size:'select', orientation:'select', font_name:'select', font_size:'select', bold:'bool', italic:'bool', color:'color',
    alignment:'align', line_spacing:'select', space_before:'text', space_after:'text', first_line_indent:'text', left_indent:'text',
  };
  const KV_SELECT_OPTS:Record<string,any> = {
    page_size: {opts:['A4','A3','A5','Letter','Legal'], labels:['A4','A3','A5','Letter','Legal']},
    orientation: {opts:['portrait','landscape'], labels:['纵向','横向']},
    font_name: {opts:FONTS},
    font_size: {opts:SIZES, labels:SIZES.map((s:number)=>s+' pt')},
    line_spacing: {opts:SPACINGS},
  };

  const renderKVForm = (data: any, prefix: string[], fieldList: string[]) => (
    <div className="grid grid-cols-4 gap-x-4 gap-y-3">
      {fieldList.map(k => {
        const ft = KV_FIELD_TYPES[k] || (typeof data?.[k]==='boolean'?'bool':'text');
        if (k==='alignment') return <div key={k}><label className="block text-[11px] font-medium text-muted-foreground mb-1">{K_LABELS[k]||k}</label>{alignCell([...prefix,k], data?.[k])}</div>;
        if (ft==='select') {
          const o = KV_SELECT_OPTS[k];
          return <div key={k}><label className="block text-[11px] font-medium text-muted-foreground mb-1">{K_LABELS[k]||k}</label>{cell([...prefix,k], data?.[k], 'select', o?.opts, o?.labels)}</div>;
        }
        return <div key={k}><label className="block text-[11px] font-medium text-muted-foreground mb-1">{K_LABELS[k]||k}</label>{cell([...prefix,k], data?.[k], ft)}</div>;
      })}
    </div>
  );

  const renderStyleTable = (data: any, prefix: string[], names: string[]) => {
    if (!data) data = {};
    const fields = STYLE_FIELDS;
    return (
      <div className="-mx-1" style={{overflow:'visible'}}>
        <table className="w-full text-xs border-collapse">
          <thead><tr className="bg-[#f8f9fb]">{['样式',...fields].map(h => <th key={h} className="py-2.5 px-2 text-left text-[11px] font-medium text-muted-foreground whitespace-nowrap first:pl-4">{K_LABELS[h]||h}</th>)}</tr></thead>
          <tbody>{names.map((name, i) => {
            const s = data[name]||{};
            return (
              <tr key={name} className={`${i%2===0?'bg-white':'bg-[#fafbfc]'} hover:bg-primary/10 transition-colors`}>
                <td className="py-2 px-2 font-medium text-foreground whitespace-nowrap pl-4 align-middle">{K_LABELS[name]||name}</td>
                {fields.map(f => {
                  const t = getFieldType(f); const [o,l] = getFieldOpts(f);
                  const narrow = ['space_before','space_after','first_line_indent','left_indent'].includes(f);
                  return <td key={f} className={`py-1 px-1 align-middle ${narrow ? 'w-[60px]' : ''}`}>{f==='alignment' ? alignCell([...prefix, name, f], s[f]) : cell([...prefix, name, f], s[f], t, o, l)}</td>;
                })}
              </tr>
            );
          })}</tbody>
        </table>
      </div>
    );
  };

  const save = async () => { if(!form.template_name.trim()){setErr('名称不能为空');return;} setSaving(true); setErr(''); try{ const fd=new FormData(); fd.append('template_name',form.template_name.trim()); fd.append('category',form.category); fd.append('industry',form.industry); fd.append('description',form.description); fd.append('is_public',String(form.is_public)); fd.append('style_info_json',JSON.stringify(si)); const r=await templateApi.updateTemplate(parseInt(id!),fd); setTpl(r); }catch(e:any){setErr(e.message||'保存失败');} finally{setSaving(false);} };
  const del = async () => { if(!id||!await confirm({ title: '确认删除', message: '确定删除？', confirmLabel: '删除', danger: true }))return; try{await templateApi.deleteTemplate(parseInt(id));navigate('/templates');}catch(e:any){toast.error(e.message||'删除失败');} };

  if (loading) return <div className="max-w-7xl mx-auto py-12 text-center text-muted-foreground">加载中...</div>;
  if (!tpl) return null;

  const CATS = ['page_setup','header_footer','heading_styles','body_style','list_styles','table_styles','toc_styles','image_styles'];

  return (
    <div className="max-w-7xl mx-auto py-4 pb-20">
      <button onClick={() => navigate('/templates')} className="text-sm text-primary hover:text-[#0042b3] mb-4 inline-block">← 返回模板列表</button>
      {err && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">{err}</div>}

      {/* 基本信息 */}
      <div className="bg-white rounded-2xl border border-border p-6 mb-4">
        <div className="flex justify-between items-start mb-4"><h1 className="text-lg font-bold">{tpl.template_name}</h1><button onClick={del} className="px-3 py-1.5 text-xs bg-red-500 text-white rounded-lg hover:bg-red-600">删除</button></div>
        <div className="grid grid-cols-4 gap-3 mb-3">
          <div><label className="block text-xs font-medium text-muted-foreground mb-1">模板名称</label><input value={form.template_name} onChange={e => setForm({...form, template_name: e.target.value})} className={inputCls} /></div>
          <div><label className="block text-xs font-medium text-muted-foreground mb-1">分类</label><input value={form.category} onChange={e => setForm({...form, category: e.target.value})} className={inputCls} /></div>
          <div><label className="block text-xs font-medium text-muted-foreground mb-1">行业</label><input value={form.industry} onChange={e => setForm({...form, industry: e.target.value})} className={inputCls} /></div>
          <div className="flex items-end"><label className="flex items-center gap-2 text-sm mb-2"><input type="checkbox" checked={form.is_public} onChange={e => setForm({...form, is_public: e.target.checked})} className="w-4 h-4 text-primary" /> 公开</label></div>
        </div>
        <input value={form.description} onChange={e => setForm({...form, description: e.target.value})} className={inputCls} placeholder="描述" />
        <p className="text-xs text-muted-foreground mt-2">创建于 {new Date(tpl.created_at).toLocaleString()}</p>
      </div>

      {/* 样式分组 */}
      {CATS.map(cat => {
        const d = si[cat]||{};
        const isStyle = ['heading_styles','list_styles','table_styles','toc_styles'].includes(cat);
        const fl = cat==='page_setup'?PAGE_FIELDS:cat==='header_footer'?HF_FIELDS:cat==='body_style'?BODY_FIELDS:cat==='image_styles'?IMG_FIELDS:[];
        return (
          <div key={cat} className="bg-white rounded-2xl border border-border mb-4">
            <div className="flex items-center gap-2 px-6 py-3 bg-[#f8f9fb] border-b border-border">
              <svg className="w-4 h-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>{cat==='page_setup'?<><path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/></>:cat==='header_footer'?<><path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 1 1 0 01.293.707V19a2 2 0 01-2 2z"/></>:<><path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h7"/></>}</svg>
              <h2 className="text-sm font-bold text-foreground">{C_LABELS[cat]||cat}</h2>
            </div>
            <div className="p-6">{isStyle ? renderStyleTable(d, [cat], STYLE_NAMES[cat]||[]) : fl.length ? renderKVForm(d, [cat], fl) : null}</div>
          </div>
        );
      })}

      {/* 固定底部保存 */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-border px-6 py-3 flex justify-end z-50">
        <button onClick={save} disabled={saving} className="px-8 py-2.5 bg-brand-700 hover:bg-[#0042b3] text-white text-sm font-semibold rounded-xl disabled:opacity-50 shadow-[0_4px_12px_rgba(0,82,217,0.2)]">{saving?'保存中...':'保存修改'}</button>
      </div>
    </div>
  );
};

export default TemplateDetailPage;
